import React, { ReactElement, useEffect, useState } from "react";
import {
	Paper,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
} from "@mui/material";
import { TableCellProps } from "@mui/material/TableCell/TableCell";
import { unstable_batchedUpdates } from "react-dom";

export interface SimpleTableColumns extends TableCellProps {
	field: string;
	headerName: string;
}

export interface TableProps<T = any> {
	loading?: boolean;
	columns: SimpleTableColumns[];
	rows: Array<T>;
	renderControls?: (id: string) => ReactElement;
	onPageChange?: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
	count?: number;
	page?: number;
	rowsPerPage?: number;
}

export default function SimpleTable({
	rows,
	columns,
	renderControls,
	rowsPerPage,
	onPageChange,
	page,
	count,
	loading = false,
}: TableProps) {
	const [innerPage, setInnerPage] = useState(0);
	const [innerRowsPerPage, setInnerRowsPerPage] = useState(15);
	const [innerCount, setInnerCount] = useState(rows.length);

	useEffect(() => {
		unstable_batchedUpdates(() => {
			if (page !== undefined) setInnerPage(page);
			if (rowsPerPage !== undefined) setInnerRowsPerPage(rowsPerPage);
			if (count !== undefined) setInnerCount(count);
		});
	}, [page, rowsPerPage, count]);

	const innerOnPageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
		onPageChange ? onPageChange(event, page) : setInnerPage(page);
	};

	return (
		<Paper elevation={4}>
			<TableContainer>
				<Table sx={{ minWidth: 650 }}>
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell key={column.field} align={column.align}>
									{column.headerName}
								</TableCell>
							))}
							{renderControls ? <TableCell /> : null}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading
							? new Array(innerRowsPerPage).fill(1).map((_, i) => (
									<TableRow key={i}>
										{columns.map((column) => (
											<TableCell key={column.headerName}>
												<Skeleton variant={"rectangular"} />
											</TableCell>
										))}
										{renderControls ? (
											<TableCell>
												<Skeleton variant={"rectangular"} />
											</TableCell>
										) : null}
									</TableRow>
							  ))
							: rows.map((row) => (
									<TableRow key={row.id}>
										{columns.map((column) => (
											<TableCell key={column.headerName} align={column.align}>
												{row[column.field]}
											</TableCell>
										))}
										{renderControls ? (
											<TableCell>{renderControls(row.parent_question ? row.parent_question : row.id)}</TableCell>
										) : null}
									</TableRow>
							  ))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				align={"right"}
				component={"div"}
				count={innerCount}
				page={innerPage}
				onPageChange={innerOnPageChange}
				rowsPerPage={innerRowsPerPage}
				rowsPerPageOptions={[15]}
			/>
		</Paper>
	);
}
